import { useState } from 'react';
import {
  Avatar,
  Button,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { PersonSettings20Regular, Settings20Regular } from '@fluentui/react-icons';
import { PreferencesDialog } from '@/features/settings/PreferencesDialog';
import { useUiStore } from '@/stores/uiStore';
import { useWhoami } from '@/hooks/useWhoami';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useGraphProfile } from '@/hooks/useGraphProfile';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';

const useStyles = makeStyles({
  trigger: {
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    borderRadius: tokens.borderRadiusCircular,
    cursor: 'pointer',
    lineHeight: 0,
    flexShrink: 0,
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
    },
  },
  surface: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minWidth: '220px',
    maxWidth: '280px',
    padding: tokens.spacingHorizontalM,
  },
  profile: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    minWidth: 0,
  },
  identity: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  name: {
    fontWeight: tokens.fontWeightSemibold,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  settingsBtn: { justifyContent: 'flex-start' },
});

export function UserMenu() {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const setView = useUiStore((s) => s.setView);
  const setSearchQuery = useUiStore((s) => s.setSearchQuery);
  const { data: me } = useWhoami();
  const profile = useUserProfile();
  const { data: graph } = useGraphProfile();

  // No identity yet → nothing to show. whoami is the auth signal the rest of the
  // pane already gates on, so this simply waits for it like everything else.
  if (!me) return null;

  // Prefer the real "Prénom NOM" from Graph; fall back to the Office display
  // name, then the UPN, so there's always something meaningful.
  const name = graph?.fullName || profile?.displayName || me.upn || 'Utilisateur';
  const photo = graph?.photoDataUrl;
  const avatarImage = photo ? { src: photo } : undefined;

  return (
    <>
    <Popover
      open={open}
      onOpenChange={(_e, d) => setOpen(d.open)}
      positioning="below-end"
      withArrow
    >
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          className={styles.trigger}
          title={name}
          aria-label={`Profil de ${name}`}
          onClick={() =>
            addBreadcrumb({ category: 'ui.click', message: 'open-user-menu' })
          }
        >
          <Avatar name={name} image={avatarImage} color="colorful" size={28} />
        </button>
      </PopoverTrigger>
      <PopoverSurface className={styles.surface}>
        <div className={styles.profile}>
          <Avatar name={name} image={avatarImage} color="colorful" size={40} />
          <div className={styles.identity}>
            <Text className={styles.name} title={name}>
              {name}
            </Text>
          </div>
        </div>
        {/* Préférences AVANT Administration, et visible par tous : c'est
            l'entrée que 100 % des utilisateurs peuvent ouvrir, alors que la
            suivante n'existe que pour les administrateurs. */}
        <Button
          appearance="subtle"
          icon={<PersonSettings20Regular />}
          className={styles.settingsBtn}
          onClick={() => {
            addBreadcrumb({ category: 'ui.click', message: 'open-preferences' });
            // Un dialogue, pas une vue : il se superpose au volet, donc rien à
            // faire de la recherche en cours ni de l'onglet actif.
            setPrefsOpen(true);
            setOpen(false);
          }}
        >
          Préférences
        </Button>
        {me.isAdmin && (
          <Button
            appearance="subtle"
            icon={<Settings20Regular />}
            className={styles.settingsBtn}
            onClick={() => {
              addBreadcrumb({ category: 'ui.click', message: 'open-settings' });
              // La page Administration masque le champ de recherche : on efface
              // une requête en cours pour ne pas afficher les résultats
              // par-dessus.
              setSearchQuery('');
              setView('settings');
              setOpen(false);
            }}
          >
            Administration
          </Button>
        )}
      </PopoverSurface>
    </Popover>
    {/* Monté HORS du Popover, et pas seulement hors du PopoverSurface : le
        popover se ferme au clic sur l'entrée, et `Popover` n'attend que ses deux
        enfants connus — un dialogue placé à l'intérieur disparaîtrait avec la
        surface ou serait ignoré. */}
    <PreferencesDialog open={prefsOpen} onOpenChange={setPrefsOpen} />
    </>
  );
}
